import { CreativeChangeDetail } from "./creativeCustomCases";
import {
  getAspectRatioDetails,
  getAssetFieldName,
  isAspectRatioPath,
  isAssetPath,
} from "./creativeSnapshotFields";
import { ChangeHistoryChange } from "./types";
import { useCreativeSnapshot } from "./useCreativeSnapshot";

type SnapshotResolved = {
  fieldName: string;
  snapshotDetails: CreativeChangeDetail[];
};

/**
 * Rows only mount when they scroll into view, so the snapshots are fetched for what
 * is on screen rather than for the whole history up front.
 */
export const useSnapshotResolvedChange = (
  change: Pick<
    ChangeHistoryChange,
    "fieldName" | "fieldPath" | "action" | "oldSnapshotId" | "newSnapshotId"
  >
): SnapshotResolved => {
  const { fieldName, fieldPath, action, oldSnapshotId, newSnapshotId } = change;

  const needsAsset = isAssetPath(fieldPath);
  const needsAspectRatio = isAspectRatioPath(fieldPath);
  const needsSnapshot = needsAsset || needsAspectRatio;

  // a removed asset is only still present in the document from before the change
  const preferredId = action === "removed" ? oldSnapshotId : newSnapshotId;
  const fallbackId = action === "removed" ? newSnapshotId : oldSnapshotId;

  const preferred = useCreativeSnapshot(
    needsSnapshot ? preferredId : undefined
  );
  // the other side is only worth fetching when it can still be read the same way
  const needsFallback = needsAspectRatio || action === "updated";
  const fallback = useCreativeSnapshot(needsFallback ? fallbackId : undefined);

  if (needsAsset) {
    // on added/removed the two documents differ by this very asset, so the other
    // side's indices mean something else and would name the row wrong
    const resolved =
      getAssetFieldName(preferred, fieldPath) ??
      (action === "updated"
        ? getAssetFieldName(fallback, fieldPath)
        : undefined);

    return { fieldName: resolved ?? fieldName, snapshotDetails: [] };
  }

  if (needsAspectRatio) {
    const before = action === "removed" ? preferred : fallback;
    const after = action === "removed" ? fallback : preferred;

    return {
      fieldName,
      snapshotDetails: getAspectRatioDetails(before, after),
    };
  }

  return { fieldName, snapshotDetails: [] };
};
